import React, { useContext, useEffect, useState } from "react";

import {
  Ion,
  Viewer,
  Cartesian3,
  Math,
  createWorldTerrainAsync,
  CesiumTerrainProvider,
  NearFarScalar,
  knockout,
  buildModuleUrl,
  UrlTemplateImageryProvider,
  Credit,
  ProviderViewModel,
  OpenStreetMapImageryProvider,
  SkyAtmosphere,
  ScreenSpaceEventType,
  JulianDate,
  TileMapServiceImageryProvider,
  BingMapsStyle,
  BingMapsImageryProvider,
  IonWorldImageryStyle,
  createWorldImageryAsync,
  ArcGisMapServerImageryProvider,
  ArcGisBaseMapType,
} from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import "cesium/Build/Cesium/Widgets/Animation/Animation.css";
import "cesium/Build/Cesium/Widgets/Animation/lighter.css";
import "cesium/Build/Cesium/Widgets/Timeline/lighter.css";
import "cesium/Build/Cesium/Widgets/lighterShared.css";
import "./3dMain.scss";
// import "./bucket.css";

import CesiumNavigation from "cesium-navigation-es6";
import { useLocalTimeClock } from "../../helper/useLocalTimeClock";

function ViewerComponent({ children }) {
  const token =
    process.env.CESIUM_TOKEN ||
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJhMDFlNjEwMC05NjU1LTRiNjktODdjMi1lYWEzZWYzMzA5MjUiLCJpZCI6MjQwMjMwLCJpYXQiOjE3MjU5MDE3ODd9.zeSmBQAblYRr52w9CrRY2Ey28OaOpXGqvx7OxJkf4uU";

  const [viewer, setViewer] = useState();

  useEffect(() => {
    buildModuleUrl.setBaseUrl(window.location.origin);
    createCesiumViewer();
  }, []);

  useEffect(() => {
    if (!viewer) return;
    let tmpViewer = viewer;
    // var scene = tmpViewer.scene;
    // var globe = scene.globe;

    //hide the moon
    tmpViewer.scene.moon.show = false;

    // Hide objects behind terrain
    tmpViewer.scene.globe.depthTestAgainstTerrain = true;
    tmpViewer.scene.screenSpaceCameraController.minimumZoomDistance = 250;
    tmpViewer.scene.fog.enabled = true;
    tmpViewer.scene.fog.density = 0.0001;
    tmpViewer.scene.fog.minimumBrightness = 1;
    tmpViewer.scene.highDynamicRange = true;

    tmpViewer.scene.globe.showGroundAtmosphere = false;
    tmpViewer.scene.globe.enableLighting = false;

    tmpViewer.screenSpaceEventHandler.removeInputAction(
      ScreenSpaceEventType.LEFT_DOUBLE_CLICK
    );

    tmpViewer.scene.skyBox.show = false;
    tmpViewer.forceResize();

    tmpViewer.scene.screenSpaceCameraController.inertiaSpin = 0.1;
    tmpViewer.scene.screenSpaceCameraController.inertiaTranslate = 0.1;
    tmpViewer.scene.screenSpaceCameraController.inertiaZoom = 0.1;

    const screenSpaceCameraController =
      viewer.scene.screenSpaceCameraController;

    // Disable camera collision to allow it to go underground
    screenSpaceCameraController.enableCollisionDetection = true;
    // screenSpaceCameraController.tiltEventTypes = 0

    tmpViewer.scene.globe.translucency.frontFaceAlphaByDistance =
      new NearFarScalar(400.0, 0.0, 800.0, 1.0);

    addNavigation(tmpViewer);
  }, [viewer]);

  const createCesiumViewer = async () => {
    Ion.defaultAccessToken = token; // Disable Cesium ion if not using it

    const viewer = new Viewer("cesiumContainer", {
      imageryProviderViewModels: generateImageryProviders(),
      baseLayerPicker: true,
      homeButton: true,
      sceneModePicker: false,
      navigationHelpButton: false,
      navigationInstructionsInitiallyVisible: false,
      infoBox: false,
      selectionIndicator: true,
      timeline: true,
      animation: false,
      scene3DOnly: true,
      geocoder: true,
      useDefaultRenderLoop: true,
      showRenderLoopErrors: true,
      automaticallyTrackDataSourceClocks: true,
      orderIndependentTranslucency: true,
      // , skyAtmosphere: false
      contextOptions: {
        webgl: {
          alpha: true,
          antialias: true,
        },
      },
      moon: true,
    });

    window.viewer = viewer;

    // var now = JulianDate.now();
    // // set time 5 minute ago
    // var newTime = JulianDate.addMinutes(now, 0, new JulianDate());

    // viewer.clock.startTime = newTime.clone();
    // viewer.clock.currentTime = newTime.clone();

    viewer.clock.shouldAnimate = true;
    useLocalTimeClock({ cesiumViewer: viewer });

    setViewer(viewer);
  };

  function addNavigation(tmpViewer) {
    const options = {};

    //default is 3
    options.duration = 4;

    options.enableCompass = true;
    options.enableZoomControls = true;
    options.enableDistanceLegend = true;
    options.enableCompassOuterRing = true;

    options.resetTooltip = "Reset View";
    options.zoomInTooltip = "Zoom In";
    options.zoomOutTooltip = "Zoom Out";

    new CesiumNavigation(tmpViewer, options);
  }

  const generateImageryProviders = () => {
    const imageryViewModels = [];

    imageryViewModels.push(
      new ProviderViewModel({
        name: "Bing Maps Aerial with Labels",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/bingAerial.png"
        ),
        tooltip: "Bing Maps Aerial with Labels",
        creationFunction: async function () {
          return await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL_WITH_LABELS,
          });
        },
      })
    );

    imageryViewModels.push(
      new ProviderViewModel({
        name: "Bing Maps Aerial",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/bingAerial.png"
        ),
        tooltip: "Bing Maps Aerial",
        creationFunction: async function () {
          return await createWorldImageryAsync({
            style: IonWorldImageryStyle.AERIAL,
          });
        },
      })
    );

    imageryViewModels.push(
      new ProviderViewModel({
        name: "Bing Maps Roads",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/bingAerial.png"
        ),
        tooltip: "Bing Maps Roads",
        creationFunction: async function () {
          return await createWorldImageryAsync({
            style: IonWorldImageryStyle.ROAD,
          });
        },
      })
    );

    imageryViewModels.push(
      new ProviderViewModel({
        name: "ArcGIS World Imagery",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/ArcGisMapServiceWorldImagery.png"
        ),
        tooltip: "ArcGIS World Imagery",
        creationFunction: async function () {
          return ArcGisMapServerImageryProvider.fromBasemapType(
            ArcGisBaseMapType.SATELLITE
          );
        },
      })
    );

    imageryViewModels.push(
      new ProviderViewModel({
        name: "ArcGIS World Ocean",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/ArcGisMapServiceWorldOcean.png"
        ),
        tooltip: "ArcGIS World Ocean",
        creationFunction: async function () {
          return ArcGisMapServerImageryProvider.fromBasemapType(
            ArcGisBaseMapType.OCEANS
          );
        },
      })
    );

    imageryViewModels.push(
      new ProviderViewModel({
        name: "Open\u00adStreet\u00adMap",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/openStreetMap.png"
        ),
        tooltip:
          "OpenStreetMap (OSM) is a collaborative project to create a free editable \
map of the world.\nhttp://www.openstreetmap.org",
        creationFunction: function () {
          return new OpenStreetMapImageryProvider({
            url: "https://tile.openstreetmap.org/",
          });
        },
      })
    );

    imageryViewModels.push(
      new ProviderViewModel({
        name: "Natural Earth\u00a0II",
        iconUrl: buildModuleUrl(
          "Widgets/Images/ImageryProviders/naturalEarthII.png"
        ),
        tooltip:
          "Natural Earth II, darkened for contrast.\nhttp://www.naturalearthdata.com/",
        creationFunction: function () {
          return TileMapServiceImageryProvider.fromUrl(
            buildModuleUrl("Assets/Textures/NaturalEarthII")
          );
        },
      })
    );

    return imageryViewModels;
  };

  return (
    <>
      <div className="no-select-item" id="igcFileDisplay">
        <div className="no-select-item" id="cesiumContainer"></div>
      </div>
      {/* <div
        className="logo-container"
        style={{ backgroundImage: `url('./logo.gif')` }}
      ></div> */}
      {viewer ? children : <></>}
    </>
  );
}

export default ViewerComponent;
